import React, { useState, useEffect } from "react";
import { Pivot, PivotItem } from "@fluentui/react";
import SyntaxHighlighter from "react-syntax-highlighter";
import styles from "./AnalysisPanel.module.css";
import { SupportingContent } from "../SupportingContent";
import { ChatAppResponse } from "../../api";
import { AnalysisPanelTabs } from "./AnalysisPanelTabs";
import { ThoughtProcess } from "./ThoughtProcess";
import { useMsal } from "@azure/msal-react";
import { getHeaders } from "../../api";
import ProductDetails from "../../pages/product/ProductDetails";
import CompetitorDetails from "../../pages/product/CompetitorDetails";
import { useLogin, getToken } from "../../authConfig";
import { PriceSource } from "../../api/models";

interface Props {
    className: string;
    activeTab: AnalysisPanelTabs;
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void;
    activeCitation: string | undefined;
    citationHeight: string;
    answer: ChatAppResponse;
    priceSource: PriceSource;
}

const pivotItemDisabledStyle = { disabled: true, style: { color: "grey" } };

export const AnalysisPanel = ({ answer, activeTab, activeCitation, citationHeight, className, onActiveTabChanged, priceSource }: Props) => {
    const [citation, setCitation] = useState("");
    const [citationContent, setCitationContent] = useState<any>(null);
    const [isJsonContent, setIsJsonContent] = useState(false);
    const isDisabledThoughtProcessTab: boolean = !answer.choices[0].context.thoughts;
    const isDisabledSupportingContentTab: boolean = !answer.choices[0].context.data_points;
    const isDisabledCitationTab: boolean = !activeCitation;
    const client = useLogin ? useMsal().instance : undefined;

    useEffect(() => {
        // Create AbortController for this fetch
        const abortController = new AbortController();

        const fetchWithAbort = async () => {
            const fetchStartTime = Date.now();
            const tokenStart = Date.now();
            const token = client ? await getToken(client) : undefined;
            const tokenTime = Date.now() - tokenStart;
            if (tokenTime > 0) {
                console.log(`[AnalysisPanel] Token retrieved in ${tokenTime}ms`);
            }

            if (activeCitation) {
                // Add timestamp to URL to prevent connection reuse issues
                const separator = activeCitation.includes("?") ? "&" : "?";
                const urlWithTimestamp = `${activeCitation}${separator}_t=${Date.now()}`;

                const headers = getHeaders(token);

                try {
                    const fetchCallStart = Date.now();
                    const response = await fetch(urlWithTimestamp, {
                        method: "GET",
                        headers: headers,
                        signal: abortController.signal
                    });
                    const fetchTime = Date.now() - fetchCallStart;
                    console.log(`[AnalysisPanel] Fetch completed in ${fetchTime}ms`);

                    const contentType = response.headers.get("content-type");

                    if (contentType && contentType.includes("application/json")) {
                        const parseStart = Date.now();
                        const jsonData = await response.json();
                        const parseTime = Date.now() - parseStart;
                        console.log(`[AnalysisPanel] JSON parsed in ${parseTime}ms`);

                        // Check if the JSON data is for a competitor
                        const isCompetitorData = jsonData[0] && jsonData[0].competitor_store;
                        setCitationContent({ data: jsonData, isCompetitorData });
                        setIsJsonContent(true);
                    } else {
                        const blobStart = Date.now();
                        const blobContent = await response.blob();
                        const blobTime = Date.now() - blobStart;
                        console.log(`[AnalysisPanel] Blob created in ${blobTime}ms`);

                        let citationObjectUrl = URL.createObjectURL(blobContent);
                        const originalHashIndex = activeCitation.indexOf("#");
                        if (originalHashIndex > -1) {
                            const originalHash = activeCitation.substring(originalHashIndex);
                            citationObjectUrl += originalHash;
                        }
                        setCitation(citationObjectUrl);
                        setIsJsonContent(false);
                    }

                    const totalTime = Date.now() - fetchStartTime;
                    console.log(`[AnalysisPanel] Citation fetch total time: ${totalTime}ms`);
                } catch (error: any) {
                    if (error.name !== "AbortError") {
                        console.error("[AnalysisPanel] Failed to fetch or parse citation", error);
                    }
                }
            }
        };

        fetchWithAbort();

        // Cleanup: abort fetch when component unmounts or activeCitation changes
        return () => {
            abortController.abort();
        };
    }, [activeCitation]);

    return (
        <Pivot
            className={className}
            selectedKey={activeTab}
            onLinkClick={pivotItem => pivotItem && onActiveTabChanged(pivotItem.props.itemKey! as AnalysisPanelTabs)}
        >
            <PivotItem
                itemKey={AnalysisPanelTabs.CitationTab}
                headerText="Product Details"
                headerButtonProps={isDisabledCitationTab ? pivotItemDisabledStyle : undefined}
            >
                <div className={styles.scrollableContent}>
                    {isJsonContent ? (
                        citationContent.isCompetitorData ? (
                            <CompetitorDetails details={citationContent.data[0]} />
                        ) : (
                            <ProductDetails details={citationContent.data} priceSource={priceSource} />
                        )
                    ) : activeCitation?.endsWith(".png") ? (
                        <img src={citation} className={styles.citationImg} />
                    ) : (
                        <iframe title="Citation" src={citation} width="100%" height={citationHeight} />
                    )}
                </div>
            </PivotItem>
            <PivotItem
                itemKey={AnalysisPanelTabs.ThoughtProcessTab}
                headerText="Thought process"
                headerButtonProps={isDisabledThoughtProcessTab ? pivotItemDisabledStyle : undefined}
            >
                <ThoughtProcess thoughts={answer.choices[0].context.thoughts || []} usage={answer.usage} />
            </PivotItem>
            <PivotItem
                itemKey={AnalysisPanelTabs.SupportingContentTab}
                headerText="Supporting content"
                headerButtonProps={isDisabledSupportingContentTab ? pivotItemDisabledStyle : undefined}
            >
                <SupportingContent supportingContent={answer.choices[0].context.data_points} />
            </PivotItem>
        </Pivot>
    );
};
